/**
 * useModalFocus — focus trap + restore for modal dialogs.
 *
 * - On open: focuses the initial element (via the returned `initialRef`).
 * - While open: traps Tab/Shift+Tab within all focusable descendants of the
 *   dialog element (via the returned `dialogRef`).
 * - On close/unmount: restores focus to the element that was focused when the
 *   modal opened.
 *
 * Usage:
 *   const { dialogRef, initialRef } = useModalFocus<HTMLDivElement, HTMLInputElement>(isOpen);
 *   <div ref={dialogRef} role="dialog" ...>
 *     <input ref={initialRef} ... />
 *   </div>
 */

import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useModalFocus<
  TDialog extends HTMLElement = HTMLDivElement,
  TInitial extends HTMLElement = HTMLElement,
>(isOpen: boolean) {
  const dialogRef = useRef<TDialog>(null);
  const initialRef = useRef<TInitial>(null);
  const previouslyFocusedRef = useRef<Element | null>(null);

  // Capture the previously focused element and restore on unmount/close.
  useEffect(() => {
    if (!isOpen) return;

    // Capture current focus before we steal it.
    previouslyFocusedRef.current = document.activeElement;

    // Focus the initial element.
    initialRef.current?.focus();

    return () => {
      // Restore focus on close / unmount.
      if (previouslyFocusedRef.current instanceof HTMLElement) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [isOpen]);

  // Tab-trap within the dialog.
  useEffect(() => {
    if (!isOpen) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;

      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.closest('[inert]'),
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  return { dialogRef, initialRef };
}
